import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";
import { RevealLines, FadeUp, ClipReveal } from "@/components/site/Reveal";
import { CONTACT } from "@/data/site";

const TIMELINE = [
  { year: "2004", name: "Garuda Productions", body: "Né d'une passion pour le cinéma, Garuda Productions proposait de la production et de la post-production de métrages et films promotionnels pour les acteurs du territoire lotois. La demande s'est ensuite élargie à d'autres moyens de communication…" },
  { year: "2010", name: "Ki-An Studio", body: "La structure évolue, affine son savoir-faire et élargit ses domaines d'expertise. Ki-An Studio propose désormais la conception de tout support de communication. Les demandes explosent." },
  { year: "Depuis 2022", name: "Equinoxe Studio", body: "Aujourd'hui, Equinoxe Studio maîtrise toutes les étapes de la promotion d'une activité, d'un évènement ou d'une entreprise. Nous analysons, créons, développons, et assurons la promotion physique et digitale." },
];

const STATS = [
  { k: "2004", l: "Année de création" },
  { k: "5", l: "Domaines d'expertise" },
  { k: "24h", l: "Réponse garantie" },
  { k: "France", l: "Prestations digitales" },
];

export default function About() {
  const bannerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: bannerRef, offset: ["start end", "end start"] });
  const bannerY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-12 pt-40 lg:pt-52 pb-20">
        <p className="mb-8 flex items-center gap-3 text-sm tracking-[0.25em] uppercase text-muted-foreground">
          <span className="h-px w-10 bg-violet" /> À propos
        </p>
        <h1 className="font-display font-extrabold tracking-tighter leading-[0.9] text-5xl sm:text-7xl lg:text-8xl max-w-5xl">
          <RevealLines lines={["Vingt ans", "de création,"]} />
          <span className="font-editorial italic font-normal text-violet block mt-2">une même équipe.</span>
        </h1>
        <FadeUp delay={0.4}>
          <p className="mt-12 max-w-3xl text-xl text-muted-foreground leading-relaxed">
            Equinoxe Studio est un studio créatif et opérationnel basé à {CONTACT.city} ({CONTACT.postal}).
            Nous accompagnons entreprises et collectivités dans la construction de leur image, de leur présence
            digitale et de leur développement commercial.
          </p>
        </FadeUp>
      </section>

      {/* STATS */}
      <section className="border-y border-black/10">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 grid grid-cols-2 lg:grid-cols-4">
          {STATS.map((s, i) => (
            <FadeUp
              key={s.l}
              delay={i * 0.08}
              className={`py-12 lg:py-16 ${i !== 0 ? "border-l border-black/10" : ""} pl-6 lg:pl-10`}
            >
              <div className="font-display font-extrabold text-4xl lg:text-6xl tracking-tighter">{s.k}</div>
              <div className="mt-3 text-muted-foreground text-sm">{s.l}</div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* BANNER */}
      <section
        ref={bannerRef}
        className="relative h-[64vh] min-h-[420px] overflow-hidden bg-ink"
        data-testid="about-banner"
      >
        <motion.img
          style={{ y: bannerY }}
          src="https://customer-assets-cm19k8pv.emergentagent.net/job_equinoxe-lot/artifacts/a6634643faf3385c_%C3%89quipe%20cr%C3%A9ative%2C%20poings%20li%C3%A9s.png"
          alt="Equinoxe Studio — une équipe soudée"
          className="absolute inset-0 h-[120%] w-full object-cover object-center -top-[10%]"
          draggable="false"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/20 to-transparent" />
        <div className="absolute inset-0 flex items-end">
          <div className="mx-auto w-full max-w-[1400px] px-6 lg:px-12 pb-14 lg:pb-20">
            <FadeUp>
              <p className="text-sm tracking-[0.25em] uppercase text-white/60 mb-4">Notre force</p>
              <h2 className="font-display font-extrabold tracking-tighter text-white text-4xl sm:text-5xl lg:text-7xl leading-[0.9] max-w-4xl">
                Soudés autour de<br />
                <span className="font-editorial italic font-normal text-violet-light">vos projets.</span>
              </h2>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-12 py-24 lg:py-40">
        <FadeUp>
          <h2 className="font-display font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-6xl mb-16">
            Notre histoire
          </h2>
        </FadeUp>
        <div className="space-y-0">
          {TIMELINE.map((t, i) => (
            <FadeUp key={t.name} delay={i * 0.1}>
              <div className="grid lg:grid-cols-12 gap-6 py-10 border-t border-black/10 items-baseline">
                <div className="lg:col-span-3 font-editorial text-2xl text-violet">{t.year}</div>
                <h3 className="lg:col-span-4 font-display font-extrabold tracking-tight text-3xl lg:text-4xl">{t.name}</h3>
                <p className="lg:col-span-5 text-muted-foreground leading-relaxed">{t.body}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* GROUPE + ZONE */}
      <section className="border-t border-black/10 bg-secondary/40">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 py-24 lg:py-32 grid lg:grid-cols-2 gap-16">
          <div>
            <p className="text-sm tracking-[0.25em] uppercase text-muted-foreground mb-6">Filiale du groupe</p>
            <ClipReveal>
              <h2 className="font-display font-extrabold tracking-tighter text-4xl lg:text-5xl">Groupe Equinoxe</h2>
            </ClipReveal>
            <FadeUp delay={0.1}>
              <p className="mt-6 text-muted-foreground leading-relaxed max-w-lg">
                Equinoxe Studio fait partie du Groupe Equinoxe, qui réunit trois pôles complémentaires : Studio
                (communication & commercial), Sécurité et Propreté. Une seule porte d'entrée, plusieurs savoir-faire.
              </p>
              <a
                href={CONTACT.groupUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-8 inline-flex items-center gap-2 bg-ink text-white px-7 py-3.5 hover:bg-violet transition-colors duration-300"
                data-testid="about-group-link"
              >
                Découvrir le groupe <ArrowUpRight size={18} />
              </a>
            </FadeUp>
          </div>

          <div>
            <p className="text-sm tracking-[0.25em] uppercase text-muted-foreground mb-6">Zone d'intervention</p>
            <div className="space-y-8">
              <FadeUp>
                <div className="flex gap-4">
                  <MapPin className="text-violet shrink-0 mt-1" />
                  <div>
                    <h3 className="font-display font-bold text-2xl tracking-tight">Terrain & tournages</h3>
                    <p className="mt-2 text-muted-foreground">
                      Interventions, rendez-vous et tournages photo/vidéo dans le Lot (46) et les départements limitrophes.
                    </p>
                  </div>
                </div>
              </FadeUp>
              <FadeUp delay={0.1}>
                <div className="flex gap-4">
                  <MapPin className="text-violet shrink-0 mt-1" />
                  <div>
                    <h3 className="font-display font-bold text-2xl tracking-tight">Digital</h3>
                    <p className="mt-2 text-muted-foreground">
                      Prestations digitales (design, web, audiovisuel) partout en France.
                    </p>
                  </div>
                </div>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-ink text-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 pt-24 lg:pt-32 pb-12 lg:pb-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
          <h2 className="font-display font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-6xl max-w-xl leading-[0.95]">
            Travaillons <span className="font-editorial italic font-normal text-violet-light">ensemble.</span>
          </h2>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 bg-white text-ink px-8 py-4 hover:bg-violet hover:text-white transition-colors duration-300"
            data-testid="about-cta"
          >
            Nous contacter <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
